// Render della Timeline (Batch 2). Produce markup nel container passato e collega
// il click delle card al detail viewer esistente (handler iniettato). La logica
// di raggruppamento/"In questo giorno" vive in `timeline.ts` (pura, testata);
// qui c'è solo presentazione. Nessun fetch: usa l'array `photos` già caricato.

import { escapeHtml, formatDate, formatTime } from "./format";
import { mediaUrl, type PhotoSummary } from "./api";
import { effectiveDate, groupByDay, onThisDay } from "./timeline";
import { icon } from "./icons";

export interface TimelineHandlers {
  /** Apre il dettaglio del ricordo selezionato (riusa il viewer esistente). */
  onSelect: (photo: PhotoSummary) => void;
}

/** Card di un ricordo. `withYear=true` mostra la data piena (usata in "In questo giorno"). */
function itemCard(p: PhotoSummary, withYear: boolean): string {
  const iso = effectiveDate(p);
  const label = withYear ? formatDate(iso) : formatTime(iso);
  const desc = p.description ? escapeHtml(p.description) : "";
  const place = p.place_label ? escapeHtml(p.place_label) : "";
  const badge =
    p.is_private === 1
      ? `<span class="tl-card__badge">${icon("lock", { size: 14, label: "Privata" })}</span>`
      : "";
  return `
    <button type="button" class="tl-card" data-id="${escapeHtml(p.id)}">
      <span class="tl-card__thumb">
        <img class="tl-card__img" src="${mediaUrl(p.id)}" alt="" loading="lazy" />
        ${badge}
      </span>
      <span class="tl-card__meta">
        ${label ? `<span class="tl-card__time">${escapeHtml(label)}</span>` : ""}
        ${desc ? `<span class="tl-card__desc">${desc}</span>` : ""}
        ${place ? `<span class="tl-card__place">${place}</span>` : ""}
      </span>
    </button>`;
}

/**
 * Renderizza la Timeline nel `container` a partire da `photos` (già filtrato a
 * monte). `today` è iniettato per testabilità della logica a valle.
 */
export function renderTimeline(
  container: HTMLElement,
  photos: PhotoSummary[],
  today: Date,
  handlers: TimelineHandlers,
): void {
  const groups = groupByDay(photos);
  const onThis = onThisDay(photos, today);

  const onThisBlock = onThis.length
    ? `<section class="tl-onthisday">
         <h2 class="tl-onthisday__title">${icon("celebration", { size: 18 })} In questo giorno</h2>
         <div class="tl-onthisday__row">${onThis.map((p) => itemCard(p, true)).join("")}</div>
       </section>`
    : "";

  const dayBlocks = groups
    .map(
      (g) => `
      <section class="tl-day">
        <h3 class="tl-day__heading">${escapeHtml(formatDate(g.dateIso))}</h3>
        <div class="tl-day__grid">${g.items.map((p) => itemCard(p, false)).join("")}</div>
      </section>`,
    )
    .join("");

  container.innerHTML = `<div class="tl">${onThisBlock}${dayBlocks}</div>`;

  container.querySelectorAll<HTMLButtonElement>(".tl-card").forEach((btn) => {
    btn.addEventListener("click", () => {
      const id = btn.dataset.id;
      const photo = photos.find((p) => p.id === id);
      if (photo) handlers.onSelect(photo);
    });
  });
}
